import { useLayoutEffect, useRef, useEffect } from 'react';

interface PageSEOInput {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: 'website' | 'article';
  ogUrl?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  jsonLd?: Record<string, unknown>[];
  lastModified?: string;
}

const SITE_URL: string = (() => {
  try {
    const env = (import.meta as unknown as { env: Record<string, string> }).env;
    return env.VITE_SITE_URL || 'https://sugargoospreadsheet.run';
  } catch {
    return 'https://sugargoospreadsheet.run';
  }
})();

function setOrCreateMeta(attr: string, value: string, property = false) {
  const attributeName = property ? 'property' : 'name';
  let el = document.querySelector(`meta[${attributeName}="${attr}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attributeName, attr);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function setOrCreateLink(rel: string, href: string) {
  let link = document.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

export const SITE_DOMAIN = SITE_URL;
export const SITE_NAME = 'SugargooSpreadsheet.run';

export function usePageSEO(props: PageSEOInput) {
  const prevSerializedRef = useRef<string>('');

  useLayoutEffect(() => {
    const {
      title, description, keywords, canonicalUrl,
      ogTitle, ogDescription, ogType, ogUrl, ogImage,
      twitterTitle, twitterDescription, jsonLd, lastModified,
    } = props;

    const serialized = JSON.stringify(props);
    if (serialized === prevSerializedRef.current) return;
    prevSerializedRef.current = serialized;

    document.title = title;

    if (description) setOrCreateMeta('description', description);
    if (keywords) setOrCreateMeta('keywords', keywords);
    if (canonicalUrl) setOrCreateLink('canonical', canonicalUrl);

    setOrCreateMeta('og:title', ogTitle || title, true);
    setOrCreateMeta('og:description', ogDescription || description || '', true);
    setOrCreateMeta('og:type', ogType || 'website', true);
    setOrCreateMeta('og:url', ogUrl || canonicalUrl || SITE_URL, true);
    setOrCreateMeta('og:site_name', SITE_NAME, true);
    if (ogImage) setOrCreateMeta('og:image', ogImage, true);

    setOrCreateMeta('twitter:card', 'summary_large_image');
    setOrCreateMeta('twitter:title', twitterTitle || title);
    setOrCreateMeta('twitter:description', twitterDescription || description || '');

    if (lastModified) setOrCreateMeta('last-modified', lastModified);

    if (jsonLd && jsonLd.length > 0) {
      document.querySelectorAll('script[data-seo-jsonld]').forEach((el) => el.remove());
      jsonLd.forEach((ld, idx) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-jsonld', `ld-${idx}`);
        script.textContent = JSON.stringify(ld);
        document.head.appendChild(script);
      });
    }
  }, [props]);

  useEffect(() => {
    return () => {
      document.querySelectorAll('script[data-seo-jsonld]').forEach((el) => el.remove());
    };
  }, []);
}